import { Dialog, DialogContent, DialogTitle } from '@ifixit/ui/components/dialog';
import { useDisclosure } from '@ifixit/ui/hooks/use-disclosure';
import { useBreakpoint } from '@ifixit/ui/hooks/use-media-query';
import { _js } from '@ifixit/utils/localize';
import { FlagSection } from './FlagSection';
import { GuideDifficulty } from './DifficultyBadge';
import { GuideStats } from './GuideStats';

interface Author {
   url: string;
   name: string;
   image: string;
   authorCount: number;
   historyUrl: string;
}

interface BaseFlag {
   flagid: string;
   // Note the image here is currently an enum, not a url
   title: string;
   text: string;
   privilegeid: number;
   fullWidth?: boolean;
}

interface ImageFlag extends BaseFlag {
   image: string;
}

interface IconFlag extends BaseFlag {
   icon: string;
}

export type Flag = ImageFlag | IconFlag;

interface ViewCounts {
   today: number;
   week: number;
   month: number;
   all: number;
}

export interface GuideData {
   title: string;
   mainImage: string;
   mainImageUrl2x: string;
   mainImageUrlFull: string;
   published_date: string;
   modified_date: string;
   isTeardown: boolean;
   guideCommentCount: number;
   stepCommentCount: number;
   viewCounts: ViewCounts;
   author: Author;

   difficulty: GuideDifficulty;
   difficultyName: string;
   timeRequired: string;
   flags: Array<Flag>;

   mainImageWidth: string;
   mainImageHeight: string;
   totalTimeIso: string;
   stepCount: number;
}

export function GuideHeading({
   author,
   modified_date,
   isTeardown,
   guideCommentCount,
   stepCommentCount,
   viewCounts,
   difficulty,
   difficultyName,
   timeRequired,
   flags,
   mainImage,
   mainImageUrl2x,
   mainImageUrlFull,
   title,
}: GuideData) {
   const isAboveMd = useBreakpoint('md');
   const isTablet = isAboveMd === false;

   const Stats = (
      <GuideStats
         isTeardown={isTeardown}
         guideCommentCount={guideCommentCount}
         stepCommentCount={stepCommentCount}
         viewCounts={viewCounts}
      />
   );

   const { isOpen, onOpen, onClose } = useDisclosure();

   return (
      <>
         <div className="flex items-start border-b border-gray-300 pb-3 mt-4 sm:mt-8">
            <img
               src={mainImage}
               srcSet={`${mainImage} 1x, ${mainImageUrl2x} 2x`}
               onClick={onOpen}
               alt={title}
               className="hidden sm:block cursor-pointer max-w-[120px] min-w-[120px] min-h-[90.5px] object-contain rounded border border-gray-300 mr-3"
            />
            <Dialog
               open={isOpen}
               onOpenChange={open => {
                  if (!open) {
                     onClose();
                  }
               }}
            >
               <DialogContent className="w-auto max-w-[calc(100%-64px)] bg-transparent border-none shadow-none p-0 gap-0">
                  <DialogTitle className="sr-only">{title}</DialogTitle>
                  <img src={mainImageUrlFull} className="w-full h-auto" alt={title} />
               </DialogContent>
            </Dialog>
            <div className="flex flex-col w-full">
               <h1 className="guide-title text-[24px] sm:text-[30px] font-medium mb-2 text-gray-800">
                  {title}
               </h1>
               <div className="flex flex-row guide-author-section">
                  <AuthorSection {...author} modified_date={modified_date} />
                  {!isTablet && <div className="flex items-center gap-3 self-center">{Stats}</div>}
               </div>
            </div>
         </div>
         {isTablet && (
            <div className="flex items-center gap-3 self-center flex-wrap pt-3 pb-3 border-b border-gray-300">
               {Stats}
            </div>
         )}
         <FlagSection
            difficultyName={difficultyName}
            difficulty={difficulty}
            timeRequired={timeRequired}
            flags={flags}
            isTeardown={isTeardown}
         />
      </>
   );
}

function AuthorSection({
   name,
   image,
   modified_date,
   authorCount,
   url,
   historyUrl,
}: Author & { modified_date: GuideData['modified_date'] }) {
   const Contributors =
      authorCount == 1
         ? `<a href="${url}" class="guide-author">${name}</a>`
         : authorCount > 2
           ? /* Translators: %1 Start of link, %2 main author name, %3 end of link, %4 start of link, %5count of other authors, %6 end of link*/
             _js(
                '%1%2%3 and %4%5 other contributors%6',
                `<a href="${url}" class="guide-author">`,
                name,
                '</a>',
                `<a href="${historyUrl}">`,
                (authorCount - 1).toString(),
                '</a>'
             )
           : /* Translators: %1 Start of link, %2 main author name, %3 end of link, %4 start of link, %5 end of link*/
             _js(
                '%1%2%3 and %4one other contributor%5',
                `<a href="${url}" class="guide-author">`,
                name,
                '</a>',
                `<a href="${historyUrl}">`,
                '</a>'
             );

   return (
      <>
         <img
            src={image}
            alt={name}
            className="size-10 rounded-full border-2 border-brand-500 bg-gray-100 object-cover"
         />
         <div className="flex flex-col self-center grow pl-1.5">
            <p
               className="m-0 text-gray-800 font-medium [&_a]:text-brand-500 [&_a:hover]:no-underline"
               dangerouslySetInnerHTML={{ __html: Contributors }}
            ></p>
            <p className="m-0 text-gray-500">{_js('Last updated on %1', modified_date)}</p>
         </div>
      </>
   );
}
